/**
 * Shared field and column builders.
 *
 * Twenty-odd document types repeat the same handful of shapes — a document
 * date, a project lookup, a quantity-times-rate line grid. Naming them once
 * keeps each definition to what is actually distinctive about that document.
 */
import type { ColumnDef, FieldDef, FilterDef, LineColumnDef, LineGridDef } from './types.js'

type W = 'full' | 'half'

export const f = {
  date: (key: string, label: string, required = false, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'date', required, width }),
  text: (key: string, label: string, width: W = 'half', required = false): FieldDef =>
    ({ key, label, control: 'text', required, width }),
  notes: (key = 'remarks', label = 'Remarks'): FieldDef =>
    ({ key, label, control: 'textarea', width: 'full' }),
  look: (key: string, label: string, entity: string, required = false, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'lookup', required, width, lookup: { entity } }),
  choice: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true, width: W = 'half'): FieldDef =>
    ({ key, label, control: 'select', required, width, options: options.map(([value, l]) => ({ value, label: l })) }),
  radio: (key: string, label: string, options: ReadonlyArray<[string, string]>, required = true): FieldDef =>
    ({ key, label, control: 'radio', required, width: 'full', options: options.map(([value, l]) => ({ value, label: l })) }),
  yesNo: (key: string, label: string, hint?: string): FieldDef =>
    ({ key, label, control: 'checkbox', width: 'half', ...(hint ? { hint } : {}) }),
  qty: (key: string, label: string, required = false): FieldDef =>
    ({ key, label, control: 'qty', required, width: 'half' }),
  money: (key: string, label: string, required = false): FieldDef =>
    ({ key, label, control: 'money', required, width: 'half' }),
}

/** The fields nearly every transactional document carries. */
export const taxUnit = (required = true): FieldDef => f.look('taxUnitId', 'Tax unit', 'tax_unit', required)
export const jobType = (): FieldDef => f.look('jobTypeId', 'Job type', 'job_type')
export const interstate = (): FieldDef =>
  f.yesNo('interstate', 'Interstate (IGST)?', 'Set from the supplier and tax unit states. Change it only if you are sure.')

/**
 * The currency a document is written in, and what it is worth (docs/20 D8).
 *
 * Put on the documents a foreign supplier or customer is a party to. The rate
 * is asked for only when the currency is not the books' own — which is the
 * whole of the multi-currency UI, because everything downstream reads the base
 * figures the server derives from these two answers.
 */
export const currencySection = (n = '95'): import('./types.js').SectionDef => ({
  key: 'currency', n, title: 'Currency',
  fields: [
    { key: 'currencyId', label: 'Currency', control: 'lookup', width: 'half',
      lookup: { entity: 'currency' },
      hint: 'Leave it empty for the currency this company keeps its books in.' },
    { key: 'forexRate', label: 'Rate against the base currency', control: 'rate', width: 'half',
      visibleWhen: { field: 'currencyId', isSet: true },
      hint: 'What one unit of that currency is worth. Needed only for a foreign currency.' },
  ],
})

/** A terms-and-conditions card: pick a template, then edit what it put there. */
export const termsSection = (n = '90'): import('./types.js').SectionDef => ({
  key: 'terms', n, title: 'Terms and conditions',
  fields: [
    f.look('termsTemplateId', 'Terms template', 'terms_template', false, 'half'),
    { key: 'termsContent', label: 'Terms', control: 'textarea', width: 'full',
      hint: 'Picking a template fills this in. Edit it here and only this document changes.' },
  ],
})

export const col = {
  seq: (): LineColumnDef => ({ key: 'seq', label: '#', width: 46 }),
  item: (label = 'Material', editable = true): LineColumnDef =>
    ({ key: 'itemId', label, type: 'text', editable }),
  desc: (): LineColumnDef => ({ key: 'description', label: 'Description', type: 'text', secondary: true, editable: true }),
  brand: (): LineColumnDef => ({ key: 'brand', label: 'Brand', type: 'text', width: 116, editable: true }),
  qty: (label = 'Qty', editable = true): LineColumnDef =>
    ({ key: 'quantity', label, type: 'qty', width: 104, align: 'right', editable }),
  /**
   * Typed, or taken from the material when one is picked. It was read-only, so
   * a line added by hand could never carry a unit at all.
   */
  unit: (editable = true): LineColumnDef => ({ key: 'unit', label: 'Unit', type: 'text', width: 86, editable }),
  rate: (label = 'Rate', editable = true): LineColumnDef =>
    ({ key: 'rate', label, type: 'rate', width: 112, align: 'right', editable }),
  discount: (): LineColumnDef => ({ key: 'discountPct', label: 'Disc %', type: 'rate', width: 92, align: 'right', editable: true }),
  amount: (label = 'Amount'): LineColumnDef => ({ key: 'amount', label, type: 'money', width: 136, align: 'right' }),
  ordered: (): LineColumnDef => ({ key: 'orderedQty', label: 'Ordered', type: 'qty', width: 104, align: 'right' }),
  open: (): LineColumnDef => ({ key: 'openQty', label: 'Still open', type: 'qty', width: 108, align: 'right' }),
  boqRef: (): LineColumnDef => ({ key: 'boqRef', label: 'BOQ ref', type: 'text', width: 120 }),
  hsn: (): LineColumnDef => ({ key: 'hsnSacCode', label: 'HSN / SAC', type: 'text', width: 118, editable: true }),
  tax: (): LineColumnDef => ({ key: 'taxPct', label: 'Tax %', type: 'rate', width: 92, align: 'right' }),
  /** docs/24 §3.8 — which rack or bin a lot went into. */
  bin: (): LineColumnDef => ({
    key: 'binId', label: 'Bin', type: 'text', width: 150, editable: true, lookup: { entity: 'storage_bin' },
    hint: 'Where in the store it was put. The lot picker and the stock count read it.',
  }),
  /**
   * Whether the customer is charged for this line.
   *
   * It is set once on the estimate and then travels — request, order, receipt,
   * invoice, challan — because the people keying those documents cannot
   * classify five hundred bolts and washers from memory, and a challan that
   * mixes the two cannot be given to a customer to sign
   * (docs/video-notes/06 §2).
   */
  billable: (label = 'Type'): LineColumnDef => ({
    key: 'isBillable', label, type: 'text', width: 124, editable: true,
    options: [{ value: 'YES', label: 'Billable' }, { value: 'NO', label: 'Non billable' }],
  }),
}

/**
 * Additional charges, and the deductions and additions applied after them.
 *
 * Every priced document in the observed system carries both, and without them
 * freight, retention and an advance recovery have nowhere to go.
 */
export const chargesGrid = (n = '80'): LineGridDef => ({
  section: 'CHARGE', title: 'Additional charges', n,
  addLabel: 'Add a charge',
  columns: [
    col.seq(),
    // Travelling, handling, transport… — items of type Charge, not materials.
    { key: 'itemId', label: 'Charge', type: 'text', editable: true, lookup: { entity: 'charge' } },
    col.desc(), col.qty(), col.rate(), col.hsn(), col.tax(), col.amount(),
  ],
  sources: [],
})

/** Which way an adjustment moves the total. Anything else reads as a deduction. */
export const ADJUSTMENT_DIRECTIONS = [
  { value: 'DEDUCTION', label: 'Deduction' },
  { value: 'ADDITION', label: 'Addition' },
] as const

/**
 * Retention, advance recovery, TDS, penalties — and the occasional addition.
 *
 * The amount is typed: an adjustment is a sum, not a quantity at a rate. It
 * used to be a quantity-times-rate preview on a grid with neither column, so
 * every adjustment was ₹0, and its direction was free text nothing read.
 */
export const adjustmentsGrid = (n = '81'): LineGridDef => ({
  section: 'ADJUSTMENT', title: 'Deductions and additions', n,
  addLabel: 'Add an adjustment',
  totalsAs: 'SUBTRACT',
  columns: [
    col.seq(),
    { key: 'adjustmentId', label: 'Adjustment', type: 'text', editable: true,
      lookup: { entity: 'invoice_adjustment', fills: { direction: 'direction' } } },
    col.desc(),
    { key: 'direction', label: 'Deduction or addition', type: 'text', width: 170, editable: true,
      options: ADJUSTMENT_DIRECTIONS },
    { key: 'rate', label: 'Amount', type: 'rate', width: 150, align: 'right', editable: true },
  ],
  sources: [],
})

/** A priced line grid: quantity times rate, less a discount. */
export const pricedLines = (title: string, n: string, itemLabel = 'Material'): LineGridDef => ({
  title, n,
  columns: [col.seq(), col.item(itemLabel), col.desc(), col.brand(), col.qty(), col.unit(), col.rate(), col.discount(), col.billable(), col.hsn(), col.tax(), col.amount()],
  sources: [
    { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
    { key: 'excel', label: 'Import from Excel', source: 'excel' },
  ],
})

/** A movement grid: quantities only, no money. Goods receipts, transfers. */
export const quantityLines = (title: string, n: string, qtyLabel = 'Quantity'): LineGridDef => ({
  title, n,
  columns: [
    col.seq(), col.item(), col.desc(), col.brand(), col.qty(qtyLabel), col.unit(),
    // A stock transfer to a customer site is a taxable event, so it is priced.
    col.rate(), col.billable(), col.hsn(), col.tax(), col.amount(),
  ],
  sources: [
    { key: 'stock', label: 'Pick from stock', source: 'stock' },
    { key: 'excel', label: 'Import from Excel', source: 'excel' },
  ],
})

/**
 * Which party a document is with. The list query exposes the joined name as
 * `supplierName` or `customerName` depending on the role, so the column and the
 * filter both have to name the same one.
 */
export type PartyKind = 'supplier' | 'subcontractor' | 'customer' | null

const partyColumnKey = (kind: PartyKind): string =>
  kind === 'customer' ? 'customerName' : 'supplierName'
const partyFilterKey = (kind: PartyKind): string =>
  kind === 'customer' ? 'customerId' : 'supplierId'

/**
 * The two columns a foreign-currency document adds (docs/00).
 *
 * Off the default view — a single-currency tenant should not carry a column of
 * INR next to a column of INR — but available in the column chooser and in
 * every export, which is where somebody reconciling a supplier statement looks.
 */
export const fcyColumns = (valueLabel = 'Total'): ColumnDef[] => [
  { key: 'currencyCode', label: 'Currency', type: 'text', width: 100 },
  { key: 'totalFcy', label: `${valueLabel} (FCY)`, type: 'money', width: 150, align: 'right' },
]

export const baseColumns = (kind: PartyKind, label = 'Party', valueLabel = 'Total'): ColumnDef[] => [
  { key: 'documentNo', label: 'Document no', type: 'docno', width: 152 },
  { key: 'documentDate', label: 'Date', type: 'date', width: 128 },
  ...(kind ? [{ key: partyColumnKey(kind), label, type: 'text' as const }] : []),
  { key: 'projectName', label: 'Project', type: 'text' },
  { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
  { key: 'total', label: valueLabel, type: 'money', width: 150, align: 'right' },
]

export const baseFilters = (kind: PartyKind, label = 'Party'): FilterDef[] => [
  { key: 'documentNo', label: 'Document no', control: 'text' },
  { key: 'documentDate', label: 'Document date', control: 'dateRange' },
  { key: 'documentStatus', label: 'Status', control: 'status' },
  ...(kind ? [{ key: partyFilterKey(kind), label, control: 'lookup' as const, lookup: { entity: kind } }] : []),
  { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
]

export const FULL_ACTIONS = ['save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone'] as const
export const BASIC_ACTIONS = ['save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print'] as const
/** For anything a supplier or a customer receives a copy of. */
export const SENDABLE_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone', 'email',
] as const

/**
 * For the money documents an accounting system takes (docs/02 §4e).
 *
 * `Re-sync to Integration` and `Stop sync to Integration` belong on the
 * document because that is where somebody notices a voucher never arrived —
 * making them chase it through an admin screen is how it stays unnoticed.
 */
export const SYNCED_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print', 'clone', 'email',
  'resync', 'stopSync', 'changeCurrency', 'changeTaxUnit',
] as const

/** The same, for the documents nobody emails out — an internal cost record. */
export const SYNCED_BASIC_ACTIONS = [
  'save', 'submit', 'approve', 'reject', 'cancel', 'update', 'print',
  'resync', 'stopSync',
] as const
export const ALL_TABS = ['summary', 'related', 'form', 'drive'] as const
export const REVISABLE_TABS = ['summary', 'related', 'revisions', 'form', 'drive'] as const
